import React from 'react';
import styled, { css } from 'styled-components';
import type { Fields } from '@puckeditor/core';

import { clampPercent, createOpacitySlider } from './opacity-slider';
import { selectField } from './widgets/select-field';
import { AssetPickerField } from './widgets/asset-picker-field';

/**
 * Flat background-image props shared by the blocks that support an image (Hero, Section, ...).
 * The image is its own control above the color controls; size, position and repeat appear only
 * once an image exists. Nothing here is nested: every value is a top-level saved prop.
 */
export type BackgroundImageProps = {
  backgroundImage: string;
  backgroundSize: string;
  backgroundPosition: string;
  backgroundRepeat: string;
  overlayOpacity: number;
};

export const BACKGROUND_SIZE_OPTIONS = [
  { label: 'Cover', value: 'cover' },
  { label: 'Contain', value: 'contain' },
  { label: 'Auto', value: 'auto' },
] as const;

export const BACKGROUND_POSITION_OPTIONS = [
  { label: 'Center', value: 'center' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Top Left', value: 'top left' },
  { label: 'Top Right', value: 'top right' },
  { label: 'Bottom Left', value: 'bottom left' },
  { label: 'Bottom Right', value: 'bottom right' },
] as const;

export const BACKGROUND_REPEAT_OPTIONS = [
  { label: 'No Repeat', value: 'no-repeat' },
  { label: 'Repeat', value: 'repeat' },
  { label: 'Repeat X', value: 'repeat-x' },
  { label: 'Repeat Y', value: 'repeat-y' },
] as const;

export const DEFAULT_BACKGROUND_IMAGE_PROPS: BackgroundImageProps = {
  backgroundImage: '',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  overlayOpacity: 50,
};

/** One fixed overlay color for every block; authors choose only its opacity. A theme may retune it. */
export const OVERLAY_HINT =
  'Darkens the image to improve text readability. It sits above the image and below your content, and does not affect the background color.';

export const BACKGROUND_OVERLAY_COLOR = 'var(--dcm-cms-background-overlay-color, #000000)';

const SIZE_VALUES: ReadonlySet<string> = new Set(BACKGROUND_SIZE_OPTIONS.map(({ value }) => value));
const POSITION_VALUES: ReadonlySet<string> = new Set(BACKGROUND_POSITION_OPTIONS.map(({ value }) => value));
const REPEAT_VALUES: ReadonlySet<string> = new Set(BACKGROUND_REPEAT_OPTIONS.map(({ value }) => value));

export const IMAGE_ONLY_FIELD_NAMES = [
  'backgroundSize',
  'backgroundPosition',
  'backgroundRepeat',
  'overlayOpacity',
] as const;

export function hasBackgroundImage(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

export function normalizeOverlayOpacity(value: unknown): number {
  return clampPercent(value, DEFAULT_BACKGROUND_IMAGE_PROPS.overlayOpacity);
}

/**
 * Inline style for the image layer. Emits nothing without an image. Unknown size/position/repeat
 * (stored JSON is unvalidated) fall back to the defaults rather than reaching the stylesheet.
 */
export function backgroundImageStyle(props: Partial<BackgroundImageProps>): React.CSSProperties {
  const { backgroundImage } = props;
  if (typeof backgroundImage !== 'string' || !hasBackgroundImage(backgroundImage)) return {};
  const escapedUrl = backgroundImage.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const pick = (value: unknown, allowed: ReadonlySet<string>, fallback: string): string =>
    typeof value === 'string' && allowed.has(value) ? value : fallback;
  return {
    backgroundImage: `url("${escapedUrl}")`,
    backgroundSize: pick(props.backgroundSize, SIZE_VALUES, DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundSize),
    backgroundPosition: pick(
      props.backgroundPosition,
      POSITION_VALUES,
      DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundPosition
    ),
    backgroundRepeat: pick(props.backgroundRepeat, REPEAT_VALUES, DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundRepeat),
  };
}

/** Overlay layer style: the fixed color at the authored opacity. Callers render it only with an image. */
export function backgroundOverlayStyle(opacity: unknown): React.CSSProperties {
  return { opacity: normalizeOverlayOpacity(opacity) / 100 };
}

/**
 * The readability scrim: a fixed-color layer above the background image and below the content.
 * It never receives pointer events and is hidden from assistive tech; opacity is the only
 * author control. Callers render it only when an image exists, as the content's earlier sibling,
 * and give the content a higher z-index so the scrim can never dim or cover it.
 */
export const BackgroundOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-color: ${BACKGROUND_OVERLAY_COLOR};
`;

/**
 * Overlay for containers whose children are a Puck slot (Flex, Grid), where an overlay element
 * cannot be inserted before the slot content. The scrim is the container's own `::before`, painted
 * at z-index -1 inside an isolated stacking context: above the container's background image and
 * below every child, with no change to the children. It exists only while the container sets the
 * `--cms-background-overlay-*` variables (see `slotBackgroundImageStyle`), i.e. with an image.
 */
export const slotBackgroundOverlayCss = css`
  &::before {
    background-color: ${BACKGROUND_OVERLAY_COLOR};
    content: var(--cms-background-overlay-content, none);
    inset: 0;
    opacity: var(--cms-background-overlay-opacity, 0);
    pointer-events: none;
    position: absolute;
    z-index: -1;
  }
`;

/** Inline style for a slot container: the image layer plus the variables that switch its scrim on. */
export function slotBackgroundImageStyle(props: Partial<BackgroundImageProps>): React.CSSProperties {
  if (!hasBackgroundImage(props.backgroundImage)) {
    // Reset the switch so a nested Flex/Grid cannot inherit an image-backed ancestor's scrim.
    return { '--cms-background-overlay-content': 'none' } as React.CSSProperties;
  }
  return {
    ...backgroundImageStyle(props),
    isolation: 'isolate',
    position: 'relative',
    '--cms-background-overlay-content': '""',
    '--cms-background-overlay-opacity': String(normalizeOverlayOpacity(props.overlayOpacity) / 100),
  } as React.CSSProperties;
}

const OverlayOpacityField = createOpacitySlider({
  testIdPrefix: 'overlay-opacity',
  fallback: DEFAULT_BACKGROUND_IMAGE_PROPS.overlayOpacity,
  hint: OVERLAY_HINT,
});

/** The background image: Library / URL tabs through the shared asset picker (lazy modal boundary kept). */
export const BACKGROUND_IMAGE_FIELD = {
  type: 'custom' as const,
  label: 'Background image',
  render: AssetPickerField,
  ai: {
    schema: { type: 'string' as const },
    stream: false,
    instructions: 'Full image URL for the background. Leave empty for no image. Do not stream partial URLs.',
  },
};

export const BACKGROUND_IMAGE_FIELDS = {
  backgroundImage: BACKGROUND_IMAGE_FIELD,
  backgroundSize: selectField('Image size', BACKGROUND_SIZE_OPTIONS, "'cover' fills the block and suits photographs."),
  backgroundPosition: selectField('Image position', BACKGROUND_POSITION_OPTIONS),
  backgroundRepeat: selectField('Image repeat', BACKGROUND_REPEAT_OPTIONS),
  overlayOpacity: {
    type: 'custom' as const,
    label: 'Image overlay opacity',
    render: OverlayOpacityField,
    ai: {
      schema: { type: 'number' as const, minimum: 0, maximum: 100 },
      instructions:
        'Opacity percentage (0 to 100) of a dark scrim between the background image and the content, used only to improve text readability over the image.',
    },
  },
};

/** Hides the image-dependent controls until an image exists. Hidden saved values are never cleared. */
export function resolveBackgroundImageFields<Props extends Partial<BackgroundImageProps>>(
  backgroundImage: unknown,
  fields: Fields<Props>
): Fields<Props> {
  if (hasBackgroundImage(backgroundImage)) return fields;
  return Object.fromEntries(
    Object.entries(fields).filter(([name]) => !(IMAGE_ONLY_FIELD_NAMES as readonly string[]).includes(name))
  ) as Fields<Props>;
}
